/** Context help for the proposal review page's question kinds (see FieldHelp).
 *  Every entry answers the same two things: what am I deciding, and what
 *  does each choice actually do to the tenant's setup. */
import type { FieldHelpEntry } from '@/components/FieldHelp';

export const QUESTION_KIND_HELP: Record<string, FieldHelpEntry> = {
  binding_choice: {
    title: 'Which field is this?',
    body:
      'The design shows sample data (a name, a date, a reference) and the importer found ' +
      'SEVERAL context fields it could map to — near-tied candidates are a human decision, ' +
      'because a wrong pick silently shows every guest the wrong value. Choosing a field ' +
      'rewrites the design so that spot renders live guest data; "Keep literal" leaves the ' +
      'sample text identical for every recipient. All confident mappings are listed under ' +
      '"bindings" on the design card.',
    story: 'design-imports',
  },
  new_tag_category: {
    title: 'New tag category?',
    body:
      'The design reads facts (a promo code, a meeting point…) that have no home in this ' +
      'tenant’s tag vocabulary yet. "Create" adds the category with every attribute listed on ' +
      'its card — vocabulary is governed, and each attribute must eventually be fed by an ' +
      'integration mapping or manual tags, or it renders blank. "Map into an existing category" ' +
      '(offered when a plausible one exists) moves the attributes there instead of minting a new ' +
      'category; any other category is reachable via Edit on the category card. "Park" creates ' +
      'nothing: the text stays exactly as designed — identical for every guest, with any drawn ' +
      '{{placeholder}} visible as plain text — until a later import or edit binds it.',
    story: 'design-imports',
  },
  attribute_merge: {
    title: 'Same fact, or a new one?',
    body:
      'The design asks for an attribute whose name is close to one that already exists. Reusing ' +
      'the existing attribute keeps one source of truth; creating a new one is right when they ' +
      'are genuinely different facts — merging different facts silently misdirects guests.',
    story: 'design-imports',
  },
  unknown_region: {
    title: 'Unclassified region',
    body:
      'This part of the design could not be attributed to a known platform feature. Keeping it ' +
      'literal ships it as static content (safe, the default); making it a tenant custom block ' +
      'lets templates reference it but grows this tenant’s bespoke block count.',
    story: 'design-imports',
  },
  block_reuse: {
    title: 'Reuse an existing block?',
    body:
      'An existing block looks very similar to this region. Reusing it avoids minting a near ' +
      'duplicate — but the existing block’s content is what will render, so pick "create" if the ' +
      'differences are intentional.',
    story: 'design-imports',
  },
  feature_proposal: {
    title: 'New platform feature?',
    body:
      'A region recurs across designs without matching any known feature. Admitting a feature is ' +
      'a three-part act (registry entry, a system block behind it, tag vocabulary to feed it) — ' +
      'parking costs nothing and the region still renders as-is.',
    story: 'design-imports',
  },
};
